import type { PulseSurveyCustomQuestionStep, PulseSurveyLikertScaleQuestionStep, PulseSurveyMultipleChoiceQuestionStep, PulseSurveySliderQuestionStep, PulseSurveyStarsQuestionStep, PulseSurveyTwoWaySliderQuestionStep } from "@/types/activities/pulse-survey";
import type { TFunction } from "react-i18next";

import DisplayType from "@core/enums/PulseSurveyQuestionDisplayType";
import PulseSurveyStepType from "@core/enums/PulseSurveyStepType";

import mapQuestionIdToDisplayType from "./mapQuestionIdToDisplayType";
import type { PulseSurveyQuestionId } from "./types";

type BuiltPulseSurveyQuestionStep =
    | Omit<PulseSurveyMultipleChoiceQuestionStep, "askFollowUpQuestion">
    | Omit<PulseSurveySliderQuestionStep, "askFollowUpQuestion">
    | Omit<PulseSurveyTwoWaySliderQuestionStep, "askFollowUpQuestion">
    | Omit<PulseSurveyStarsQuestionStep, "askFollowUpQuestion">
    | Omit<PulseSurveyLikertScaleQuestionStep, "askFollowUpQuestion">
    | Omit<PulseSurveyCustomQuestionStep, "askFollowUpQuestion">;

const buildPulseSurveyQuestionStep = (questionId: PulseSurveyQuestionId, t: TFunction<"activities">): BuiltPulseSurveyQuestionStep => {
    const utils = {
        getQuestion: (id: PulseSurveyQuestionId) => t(`pulseSurveyQuestions.${id.toLowerCase()}`),
        getMinLabel: (id: PulseSurveyQuestionId) => t(`pulseSurveyQuestions.${id.toLowerCase()}-min-label`),
        getMaxLabel: (id: PulseSurveyQuestionId) => t(`pulseSurveyQuestions.${id.toLowerCase()}-max-label`),
        getConstructiveFollowUpQuestion: (id: PulseSurveyQuestionId) => t(`pulseSurveyQuestions.${id.toLowerCase()}-follow-up-constructive`),
        getPositiveFollowUpQuestion: (id: PulseSurveyQuestionId) => t(`pulseSurveyQuestions.${id.toLowerCase()}-follow-up-positive`),
        getImageUrls: (id: PulseSurveyQuestionId, indexes: number[]) => indexes.map(index => `${window.env.CDN_URL}/activities/pulse-survey/images/${id}-${index}.webp`),
        getLabels: (id: PulseSurveyQuestionId, indexes: number[]) => indexes.map(index => t(`pulseSurveyQuestions.${id.toLowerCase()}-choice-label-${index}`)),
        getLikertLabels: () => [
            t("pulseSurveyQuestions.likert-scale-strongly-disagree"),
            t("pulseSurveyQuestions.likert-scale-disagree"),
            t("pulseSurveyQuestions.likert-scale-neutral"),
            t("pulseSurveyQuestions.likert-scale-agree"),
            t("pulseSurveyQuestions.likert-scale-strongly-agree")
        ]
    };

    const displayType = mapQuestionIdToDisplayType(questionId);

    const commonData = {
        questionId,
        question: "",
        constructiveFollowUpQuestion: "",
        positiveFollowUpQuestion: "",
        displayType,
        stepType: PulseSurveyStepType.AskQuestion
    };

    if (questionId === "Q-CUSTOM") {
        return {
            ...commonData,
            customQuestionId: questionId,
            displayType: DisplayType.Custom,
            stepType: PulseSurveyStepType.AskCustomQuestion,
            feedback: null,
            isAnonymous: true
        };
    }

    commonData.question = utils.getQuestion(questionId);
    commonData.constructiveFollowUpQuestion = utils.getConstructiveFollowUpQuestion(questionId);
    commonData.positiveFollowUpQuestion = utils.getPositiveFollowUpQuestion(questionId);

    switch (displayType) {
        case DisplayType.Slider:
            return {
                ...commonData,
                displayType: DisplayType.Slider,
                minLabel: utils.getMinLabel(questionId),
                maxLabel: utils.getMaxLabel(questionId)
            };
        case DisplayType.TwoWaySlider:
            return {
                ...commonData,
                displayType: DisplayType.TwoWaySlider,
                minLabel: utils.getMinLabel(questionId),
                maxLabel: utils.getMaxLabel(questionId),
                imageUrls: utils.getImageUrls(questionId, [1, 2, 3, 4, 5])
            };
        case DisplayType.LikertScale:
            return {
                ...commonData,
                displayType: DisplayType.LikertScale,
                labels: utils.getLikertLabels()
            } ;
        case DisplayType.MultipleChoice:
            return {
                ...commonData,
                displayType: DisplayType.MultipleChoice,
                labels: utils.getLabels(questionId, [1, 2, 3, 4]),
                imageUrls: utils.getImageUrls(questionId, [1, 2, 3, 4]),
                customAnswer: null
            };
        case DisplayType.Stars:
            return {
                ...commonData,
                displayType: DisplayType.Stars
            };
        default:
            throw new Error(`Display Type (${displayType}) not supported`);
    }
};

export default buildPulseSurveyQuestionStep;